import React, { useState, useEffect } from "react";
import T from '../../components/common/T';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useInventory } from "../../context/InventoryContext";
import { useProjects } from "../../context/ProjectContext";
import { useTasks } from "../../context/TaskContext";
import { useConsumption } from "../../context/ConsumptionContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';

export default function MaterialIssueForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { inventories, warehouses } = useInventory();
  const { projects } = useProjects();
  const { tasks } = useTasks();
  const { issueMaterial } = useConsumption();

  // MaterialIssueForm is a local-context operation with no backend record ID
  // Custom fields are stored with null record_id until a backend save is wired up
  const { customData, setCustomData } = useCustomFields('inventory', null);

  const [formData, setFormData] = useState({
    projectId: "",
    projectName: "",
    taskId: "",
    taskName: "",
    materialId: "",
    materialName: "",
    warehouseId: "",
    bin: "",
    quantity: "",
    notes: ""
  });

  const [availableLocations, setAvailableLocations] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);

  useEffect(() => {
    if (location.state) {
        const { prefillProjectId, prefillMaterialId, prefillQuantity, prefillNotes } = location.state;

        if (prefillProjectId) {
            const p = projects.find(proj => String(proj.id) === String(prefillProjectId));
            setFormData(prev => ({ 
                ...prev, 
                projectId: prefillProjectId, 
                projectName: p ? p.name : "" 
            }));
        }

        if (prefillMaterialId) {
             const m = inventories.find(inv => String(inv.id) === String(prefillMaterialId));
             setFormData(prev => ({
                 ...prev,
                 materialId: prefillMaterialId,
                 materialName: m ? m.itemName : "",
                 quantity: prefillQuantity || "",
                 notes: prefillNotes || ""
             }));
        }
    }
  }, [location.state, projects, inventories]);

  useEffect(() => {
    if (formData.projectId) {
        const pTasks = tasks.filter(t => String(t.projectId) === String(formData.projectId));
        setProjectTasks(pTasks);
    } else {
        setProjectTasks([]);
    }
  }, [formData.projectId, tasks]);

  useEffect(() => {
    if (formData.materialId) {
        const item = inventories.find(i => String(i.id) === String(formData.materialId));
        if (item && item.locations) {
            setAvailableLocations(item.locations);
        } else {
            setAvailableLocations([]);
        }
    }
  }, [formData.materialId, inventories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "projectId") {
        const p = projects.find(proj => String(proj.id) === String(value));
        setFormData(prev => ({ ...prev, projectId: value, projectName: p ? p.name : "" }));
    } else if (name === "materialId") {
        const m = inventories.find(inv => String(inv.id) === String(value));
        setFormData(prev => ({ 
            ...prev, 
            materialId: value, 
            materialName: m ? m.itemName : "",
            unitPrice: m ? (m.unitPrice || 0) : 0, 
            warehouseId: "", 
            bin: "" 
        }));
    } else if (name === "taskId") {
        const t = tasks.find(task => String(task.id) === String(value));
        setFormData(prev => ({ ...prev, taskId: value, taskName: t ? t.title : "" }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLocationChange = (e) => {
      const [whId, binVal] = e.target.value.split("|");
      setFormData(prev => ({ ...prev, warehouseId: whId, bin: binVal }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.projectId || !formData.materialId || !formData.warehouseId || !formData.quantity) {
        toast.error("Please fill all required fields");
        return;
    }

    try {
        issueMaterial(formData);
        toast.success("✅ Material Issued Successfully!");
        setTimeout(() => navigate("/admin/inventory"), 1500);
    } catch (err) {
        toast.error("Failed to issue material");
    }
  };

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen flex justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 w-full max-w-3xl">
            <h2 className="text-base font-bold text-gray-800 mb-6 border-b pb-4"><T>Issue Material to Site</T></h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2"><T>Select Project </T><span className="text-red-500">*</span></label>
                        <select name="projectId" value={formData.projectId} onChange={handleChange} className="w-full border p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                            <option value=""><T>-- Choose Project --</T></option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2"><T>Select Task (Optional)</T></label>
                        <select name="taskId" value={formData.taskId} onChange={handleChange} className="w-full border p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none" disabled={!formData.projectId}>
                            <option value=""><T>-- General Usage --</T></option>
                            {projectTasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2"><T>Select Material </T><span className="text-red-500">*</span></label>
                    <select name="materialId" value={formData.materialId} onChange={handleChange} className="w-full border p-2.5 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                        <option value=""><T>-- Choose Item --</T></option>
                        {inventories.map(inv => <option key={inv.id} value={inv.id}>{inv.itemName} (Total: {inv.totalQuantity})</option>)}
                    </select>
                </div>

                {formData.materialId && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <h4 className="text-base font-bold text-blue-800 mb-3"><T>Stock Source</T></h4>
                        
                        {availableLocations.length === 0 ? (
                            <p className="text-red-500 text-sm font-medium"><T>⚠ No stock available for this item.</T></p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Source Bin</T></label>
                                    <select onChange={handleLocationChange} className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500">
                                        <option value=""><T>-- Select Source --</T></option>
                                        {availableLocations.map((loc, idx) => {
                                            const whName = warehouses.find(w => String(w.id) === String(loc.warehouseId))?.name || loc.warehouseId;
                                            return (
                                                <option key={idx} value={`${loc.warehouseId}|${loc.bin}`}>
                                                    {whName} - Bin: {loc.bin} (Avail: {loc.qty})
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Issue Quantity</T></label>
                                    <input 
                                        type="number" 
                                        name="quantity" 
                                        value={formData.quantity} 
                                        onChange={handleChange}
                                        className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2"><T>Notes / Reference</T></label>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} rows="2" className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. Urgent requirement for pouring..."></textarea>
                </div>

                {/* Custom Fields */}
                <CustomFieldRenderer module="inventory" customData={customData} onChange={setCustomData} />

                <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={() => navigate("/admin/inventory")} className="px-6 py-2 border rounded-lg hover:bg-gray-50"><T>Cancel</T></button>
                    <button type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-lg"><T>Confirm Issuance</T></button>
                </div>
            </form>
        </div>
      </div>
    </>
  );
}